import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

export function ReceiptAccountDialog({ receipt, companyId, onClose, onSaved }) {
  const [accounts, setAccounts] = useState([]);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveRule, setSaveRule] = useState(false);
  const [warn, setWarn] = useState(null); // {account, hint, better, previous}

  useEffect(() => {
    if (!receipt || !companyId) return;
    apiClient.get(`/companies/${companyId}/accounts`).then((r) => setAccounts(r.data || [])).catch(console.error);
    setFilter("");
    setWarn(null);
    setSaveRule(false);
  }, [receipt, companyId]);

  const ruleVendor = receipt?.merchant_name_ai || receipt?.vendor_raw || receipt?.description || "";

  const save = async (accountStr, confirmed) => {
    setSaving(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/receipts/${receipt._id}/account`, {
        account: accountStr,
        confirmed: !!confirmed,
      });
      if (r.data?.warning) {
        setWarn({ account: accountStr, ...r.data.warning, previous: r.data.previous_account });
        return;
      }
      toast.success(`Konto geändert: ${accountStr} — gilt beim Buchen und im Bank-Abgleich`);
      if (saveRule && ruleVendor) {
        const code = ((accountStr || "").match(/\d{3,5}/) || [])[0];
        if (code) {
          try {
            await apiClient.post(`/companies/${companyId}/bank/booking-rules`, {
              vendor_name: ruleVendor,
              account_id: code,
            });
            toast.success(`📌 Regel gespeichert: «${ruleVendor}» → Konto ${code} (jederzeit änderbar)`);
          } catch (err) {
            toast.error("Regel konnte nicht gespeichert werden");
          }
        }
      }
      if (r.data?.learning) {
        toast.info("KI lernt aus deiner Korrektur und verfeinert die Konto-Beschreibung.", { duration: 5000 });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konto konnte nicht geändert werden");
    } finally {
      setSaving(false);
    }
  };

  const pick = (acc) => {
    if (saving) return;
    save(`${acc.code} ${acc.name}`, false);
  };

  const q = filter.toLowerCase();
  const currentCode = ((receipt?.suggested_account || receipt?.account_id || "").match(/\d{3,5}/) || [])[0];
  const GROUPS = {
    1: "Aktiven", 2: "Passiven", 3: "Ertrag", 4: "Material & Waren",
    5: "Personalaufwand", 6: "Betriebsaufwand", 7: "Nebenerfolg", 8: "Ausserordentlich", 9: "Abschluss",
  };
  const list = accounts
    .filter((a) => !q || `${a.code} ${a.name} ${a.description || ""}`.toLowerCase().includes(q))
    .sort((a, b) => Number(a.code) - Number(b.code))
    .slice(0, 80);

  let lastGroup = null;

  return (
    <Dialog open={!!receipt} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="receipt-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-violet-700" />
            Konto ändern — {receipt?.description || "Beleg"}
          </DialogTitle>
        </DialogHeader>
        {warn ? (
          <div className="space-y-3" data-testid="account-plausibility-warning">
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3">
              <div className="text-sm font-semibold text-amber-900">Bist du sicher?</div>
              <div className="mt-1 text-sm text-amber-800">
                Die KI sieht das anders: {warn.hint || `„${warn.account}" passt möglicherweise nicht zu diesem Beleg.`}
              </div>
              {warn.better && (
                <div className="mt-1 text-xs text-amber-700">
                  KI-Vorschlag: <b>{warn.better}</b>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              {warn.better && (
                <Button
                  className="bg-violet-700 hover:bg-violet-800 w-full"
                  disabled={saving}
                  onClick={() => save(warn.better, true)}
                  data-testid="plausibility-take-suggestion"
                >
                  KI-Vorschlag übernehmen: {warn.better}
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full"
                disabled={saving}
                onClick={() => save(warn.account, true)}
                data-testid="plausibility-keep-anyway"
              >
                Ja, ich bin sicher — {warn.account} behalten
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                disabled={saving || !warn.previous}
                onClick={() => save(warn.previous, true)}
                data-testid="plausibility-revert"
              >
                Zurück zu {warn.previous || "vorherigem Konto"}
              </Button>
            </div>
          </div>
        ) : (
        <div className="space-y-2 min-w-0 overflow-hidden">
          <div className="text-xs text-slate-500">
            Aktuell: <b>{receipt?.suggested_account || receipt?.account_id || "—"}</b>. Das neue Konto
            wird beim Buchen und im Bank-Abgleich verwendet — die KI merkt sich deine Korrektur.
          </div>
          <Input
            autoFocus
            placeholder="Konto suchen (Nummer oder Name)…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="receipt-account-filter-input"
          />
          <label
            className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"
            title="Merkt sich die Gegenpartei — künftige Belege und Bankzahlungen werden automatisch mit diesem Konto vorgeschlagen. Jederzeit in den Buchungsregeln änderbar."
            data-testid="receipt-save-rule-checkbox"
          >
            <input
              type="checkbox"
              checked={saveRule}
              onChange={(e) => setSaveRule(e.target.checked)}
              className="accent-slate-900"
            />
            📌 Immer so buchen — Regel für «{ruleVendor || "Gegenpartei"}» speichern
          </label>
          <div className="max-h-[45vh] overflow-y-auto overflow-x-hidden min-w-0">
            {list.map((a) => {
              const group = GROUPS[String(a.code)[0]] || "Weitere";
              const showHeader = group !== lastGroup;
              lastGroup = group;
              const isCurrent = String(a.code) === currentCode;
              return (
                <React.Fragment key={a._id}>
                  {showHeader && (
                    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-slate-400 px-1 pt-2 pb-1 border-b border-slate-100">
                      {group}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => pick(a)}
                    disabled={saving || isCurrent}
                    className={`w-full text-left rounded-md px-3 py-1.5 text-sm border transition-colors ${
                      isCurrent
                        ? "border-violet-300 bg-violet-50 cursor-default"
                        : "border-transparent hover:bg-violet-50 hover:border-violet-200"
                    }`}
                    data-testid={`receipt-account-option-${a.code}`}
                  >
                    <span className="flex items-center justify-between gap-2 min-w-0">
                      <span className="min-w-0 block">
                        <span className="font-mono font-semibold text-slate-700">{a.code}</span>
                        <span className="text-slate-800"> · {a.name}</span>
                        {a.description && (
                          <span className="block text-[11px] text-slate-400 truncate min-w-0">{a.description}</span>
                        )}
                      </span>
                      {isCurrent && (
                        <span className="flex-shrink-0 rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-medium text-white">
                          Aktuell
                        </span>
                      )}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
            {list.length === 0 && <div className="text-sm text-slate-400 py-3 px-1">Kein Konto gefunden.</div>}
          </div>
          {saving && (
            <div className="text-xs text-violet-700 flex items-center gap-1.5" data-testid="account-checking-indicator">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-violet-300 border-t-violet-700 animate-spin" />
              KI prüft, ob das Konto zum Beleg passt…
            </div>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
